import { useEffect, useState } from "react";
import { PiggyBank } from "lucide-react";
import { useAdminTranslation } from "@/i18n/hooks";
import { useAuth } from "@/context/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch, SwitchField } from "@/components/tailwind/switch";
import { Label, Description } from "@/components/tailwind/fieldset";
import {
    getSocialAccountEnabled,
    updateSocialAccountEnabled,
    getOverdraftAllowed,
    updateOverdraftAllowed
} from "@/services/social-account-settings-services";
import log from '@/lib/logger'

export default function SectionSocialAccount() {
    const { t } = useAdminTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [overdraftAllowed, setOverdraftAllowed] = useState(false);

    useEffect(() => {
        loadSettings();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadSettings() {
        if (!token) return;

        setIsLoading(true);
        try {
            const [enabledValue, overdraftValue] = await Promise.all([
                getSocialAccountEnabled(token),
                getOverdraftAllowed(token)
            ]);
            setEnabled(enabledValue);
            setOverdraftAllowed(overdraftValue);
        } catch (error) {
            log.error("Error loading social account settings:", error);
            setEnabled(false);
            setOverdraftAllowed(false);
        } finally {
            setIsLoading(false);
        }
    }

    async function persistToggle(
        value: boolean,
        previous: boolean,
        setter: (value: boolean) => void,
        update: (token: string, value: boolean) => Promise<boolean>
    ) {
        if (!token) return;

        setter(value);
        setIsSaving(true);
        try {
            await update(token, value);
            toast({
                title: t('adminSettings.socialAccount.toast.success.title'),
                description: t('adminSettings.socialAccount.toast.success.description'),
                variant: "default"
            });
        } catch (error) {
            log.error("Error saving social account setting:", error);
            setter(previous);
            toast({
                title: t('adminSettings.socialAccount.toast.error.title'),
                description: t('adminSettings.socialAccount.toast.error.description'),
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    }

    function handleEnabledToggle(value: boolean) {
        persistToggle(value, enabled, setEnabled, updateSocialAccountEnabled);
    }

    function handleOverdraftToggle(value: boolean) {
        persistToggle(value, overdraftAllowed, setOverdraftAllowed, updateOverdraftAllowed);
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <PiggyBank className="h-5 w-5" />
                    {t('adminSettings.socialAccount.title')}
                </CardTitle>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t('adminSettings.socialAccount.loading')}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <SwitchField>
                            <Label>{t('adminSettings.socialAccount.enabled.label')}</Label>
                            <Description>
                                {t('adminSettings.socialAccount.enabled.description')}
                            </Description>
                            <Switch
                                checked={enabled}
                                onChange={handleEnabledToggle}
                                disabled={isSaving}
                                color="blue"
                                data-testid="social-account-enabled-switch"
                            />
                        </SwitchField>

                        <SwitchField>
                            <Label>{t('adminSettings.socialAccount.overdraft.label')}</Label>
                            <Description>
                                {t('adminSettings.socialAccount.overdraft.description')}
                            </Description>
                            <Switch
                                checked={overdraftAllowed}
                                onChange={handleOverdraftToggle}
                                disabled={isSaving || !enabled}
                                color="blue"
                                data-testid="social-account-overdraft-switch"
                            />
                        </SwitchField>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
